// §6.4 SpeciesCard (list) — the primary "here's a species" row that
// stacks on cream. Card lives on --abyss with the abyss shadow; 78px
// thumb (radius 12) in a SceneFallback/PhotoFrame; Fraunces 600 name +
// italic latin, chip row underneath.

import type { ReactNode } from "react";
import { PhotoFrame } from "./PhotoFrame";

export interface SpeciesRowProps {
  name: string;
  latin: string;
  photo?: string;
  chips?: ReactNode;
  onClick?: () => void;
  onPhotoClick?: () => void;
  trailing?: ReactNode;
  className?: string;
  /** Extra content below the chip row (e.g. a per-user note). */
  children?: ReactNode;
  /**
   * Above-the-fold hint. Consumers rendering a list can set this on the
   * first 4–5 rows so those photos load with fetchpriority=high — visibly
   * faster first paint on cellular without penalising the rows below.
   */
  priority?: boolean;
}

export function SpeciesRow({
  name,
  latin,
  photo,
  chips,
  onClick,
  onPhotoClick,
  trailing,
  className = "",
  children,
  priority = false,
}: SpeciesRowProps) {
  const fetchPriority = priority ? "high" : undefined;
  // Nested interactive elements are invalid HTML — screen readers double
  // up focus and iOS Safari sometimes drops taps. Render the row as a
  // plain container and put the row-tap onto a text-area button next to
  // the photo button. Only ONE interactive element per subtree.
  const photoSlot = onPhotoClick && photo ? (
    <button
      type="button"
      onClick={onPhotoClick}
      aria-label={`Enlarge photo of ${name}`}
      className="group/photo relative h-[78px] w-[78px] flex-none rounded-thumb focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-aqua-deep"
    >
      <PhotoFrame
        src={photo}
        alt={name}
        hint={latin}
        className="h-full w-full"
        sceneSimple
        size="square"
        fetchPriority={fetchPriority}
      />
      <span
        aria-hidden
        className="pointer-events-none absolute bottom-1 right-1 rounded-md bg-abyss/70 px-1.5 py-0.5 text-[9px] font-bold leading-none text-paper-ink opacity-0 transition group-hover/photo:opacity-100 group-focus-visible/photo:opacity-100"
      >
        ↗
      </span>
    </button>
  ) : (
    <PhotoFrame
      src={photo}
      alt={name}
      hint={latin}
      className="aspect-square h-[78px] w-[78px] flex-none"
      sceneSimple
      size="square"
      fetchPriority={fetchPriority}
    />
  );
  // The text column: when the row itself is tappable, it's a button.
  // When it isn't, it's a plain div. Either way it's a sibling of the
  // photo button, never a parent.
  const textCol = onClick ? (
    <button
      type="button"
      onClick={onClick}
      className="min-w-0 flex-1 text-left transition hover:brightness-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-aqua-deep rounded-md"
    >
      <p className="truncate font-display text-[16.5px] font-semibold leading-[1.15] text-paper-ink">
        {name}
      </p>
      <p className="truncate text-[12px] italic text-paper-ink/55">{latin}</p>
      {chips && (
        <div className="mt-2 flex flex-wrap items-center gap-1.5">{chips}</div>
      )}
      {children}
    </button>
  ) : (
    <div className="min-w-0 flex-1 text-left">
      <p className="truncate font-display text-[16.5px] font-semibold leading-[1.15] text-paper-ink">
        {name}
      </p>
      <p className="truncate text-[12px] italic text-paper-ink/55">{latin}</p>
      {chips && (
        <div className="mt-2 flex flex-wrap items-center gap-1.5">{chips}</div>
      )}
      {children}
    </div>
  );
  return (
    <div
      className={`flex items-center gap-3 rounded-card bg-abyss p-[11px] shadow-abyss ${className}`}
    >
      {photoSlot}
      {textCol}
      {trailing && <div className="flex-none">{trailing}</div>}
    </div>
  );
}
